import { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Skeleton } from '@/components/ui/skeleton';
import { GradeNumeros, ItemLista } from '@/components/common/ListaMobile';
import { useResolveError, useAffectedDevices } from '@/hooks/useErrors';
import {
  Flame,
  AlertCircle,
  AlertTriangle,
  Check,
  RefreshCw,
  Smartphone,
  User,
  Calendar,
  Code,
  Copy,
  Layers,
} from 'lucide-react';
import type { ErrorSeverity, AppError } from '@/types/error.types';
import { formatDateTime } from '@/utils/formatters';

interface ErrorDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: AppError;
}

const severityConfig: Record<string, { label: string; icon: typeof Flame; chip: string }> = {
  critical: { label: 'Crítico', icon: Flame, chip: 'bg-error/10 text-error' },
  high: { label: 'Alto', icon: AlertCircle, chip: 'bg-warning/10 text-warning' },
  medium: { label: 'Médio', icon: AlertTriangle, chip: 'bg-muted text-text-secondary' },
  low: { label: 'Baixo', icon: AlertTriangle, chip: 'bg-muted text-text-secondary' },
  error: { label: 'Erro', icon: AlertCircle, chip: 'bg-error/10 text-error' },
};

const getSeverity = (severity: ErrorSeverity) => severityConfig[severity] ?? severityConfig.medium;

export function ErrorDetailsModal({ isOpen, onClose, error }: ErrorDetailsModalProps) {
  const {
    data: affectedDevices,
    isLoading: isLoadingDevices,
    error: devicesError,
  } = useAffectedDevices(error.id);
  const resolveError = useResolveError();
  const [notes, setNotes] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleResolve = async () => {
    setIsResolving(true);
    try {
      await resolveError.mutateAsync({ id: error.id, notes: notes || undefined });
      onClose();
    } catch (err) {
      console.error('Failed to resolve error:', err);
      alert('Erro ao marcar como resolvido. Verifique se a API está rodando.');
    } finally {
      setIsResolving(false);
    }
  };

  const isResolved = error.status === 'resolved';
  const sev = getSeverity(error.severity);
  const SevIcon = sev.icon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalhes do Erro" size="xl">
      <div className="p-6 max-md:p-4">
        {/* Header Info */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex w-full flex-col gap-3">
            <div className="flex items-center gap-3">
              <span
                className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${sev.chip}`}
              >
                <SevIcon size={20} />
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-lg font-semibold text-text-title">{sev.label}</span>
                {isResolved ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
                    <Check size={14} />
                    Resolvido
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-error/10 px-3 py-1 text-xs font-medium text-error">
                    Não resolvido
                  </span>
                )}
              </div>
            </div>

            {/* Error Message in styled container */}
            <div className="relative overflow-hidden rounded-xl border border-border-strong bg-card shadow-sm">
              <div className="flex items-center justify-between gap-2 bg-muted px-5 py-3 max-md:px-4">
                <div className="flex items-center gap-2">
                  <Code size={16} className="text-text-secondary" />
                  <span className="text-sm font-medium text-text-title">Detalhes do Erro</span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(error.message);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    } catch (err) {
                      console.error('Failed to copy error:', err);
                    }
                  }}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                    copied
                      ? 'bg-success text-on-tertiary shadow-md'
                      : 'border border-border-strong bg-card text-text-secondary hover:bg-muted hover:text-text-title'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check size={14} />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copiar código
                    </>
                  )}
                </button>
              </div>
              <div className="overflow-x-auto bg-muted/40 p-6 max-md:p-4">
                <pre className="whitespace-pre-wrap break-words font-mono text-sm leading-relaxed text-text-title">
                  <code>{error.message}</code>
                </pre>
              </div>
            </div>

            {/* No celular, uma data por linha; a partir de 1024px, lado a lado */}
            <div className="flex flex-col gap-1.5 text-sm text-text-secondary lg:flex-row lg:items-center lg:gap-4">
              <span className="flex items-center gap-1">
                <Calendar size={16} className="shrink-0" />
                Primeira ocorrência: {formatDateTime(error.firstOccurrence)}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={16} className="shrink-0" />
                Última ocorrência: {formatDateTime(error.lastOccurrence)}
              </span>
            </div>
          </div>
        </div>

        {/* Stats — no celular, bloco de 3 números (os cartões cortavam o texto) */}
        <GradeNumeros
          className="mb-6 lg:hidden"
          itens={[
            { rotulo: 'Ocorrências', valor: (error.occurrences || 0).toLocaleString('pt-BR') },
            { rotulo: 'Dispositivos', valor: error.affectedDevices || 0 },
            {
              rotulo: 'Tipo',
              valor: (
                <span className="block truncate text-base capitalize">{error.type || 'N/A'}</span>
              ),
            },
          ]}
        />
        <div className="mb-6 hidden grid-cols-3 gap-4 lg:grid">
          <div className="rounded-xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-text-secondary">Ocorrências</p>
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Layers size={16} />
              </span>
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums text-text-title">
              {(error.occurrences || 0).toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-text-secondary">Dispositivos</p>
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Smartphone size={16} />
              </span>
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums text-text-title">
              {error.affectedDevices || 0}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-text-secondary">Tipo</p>
              <span
                className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${sev.chip}`}
              >
                <SevIcon size={16} />
              </span>
            </div>
            <p className="mt-2 text-2xl font-bold capitalize text-text-title">
              {error.type || 'N/A'}
            </p>
          </div>
        </div>

        {/* Versions */}
        {error.affectedVersions && error.affectedVersions.length > 0 && (
          <div className="mb-6">
            <h4 className="mb-2 text-sm font-semibold text-text-title">Versões Afetadas</h4>
            <div className="flex flex-wrap gap-2">
              {error.affectedVersions.map((version) => (
                <span
                  key={version}
                  className="rounded-lg bg-muted px-3 py-1 font-mono text-sm text-text-title"
                >
                  {version}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stack Trace */}
        {error.stackTrace && (
          <div className="mb-6">
            <div className="relative overflow-hidden rounded-xl border border-border-strong bg-card shadow-sm">
              {/* Header */}
              <div className="flex items-center justify-between gap-2 bg-muted px-5 py-3 max-md:px-4">
                <div className="flex items-center gap-2">
                  <Code size={16} className="text-text-secondary" />
                  <span className="text-sm font-medium text-text-title">Stack Trace</span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(error.stackTrace || '');
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    } catch (err) {
                      console.error('Failed to copy stack trace:', err);
                    }
                  }}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                    copied
                      ? 'bg-success text-on-tertiary shadow-md'
                      : 'border border-border-strong bg-card text-text-secondary hover:bg-muted hover:text-text-title'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check size={14} />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copiar código
                    </>
                  )}
                </button>
              </div>
              {/* Code Block */}
              <div className="overflow-x-auto bg-muted/40 p-6 max-md:p-4">
                <pre className="font-mono text-sm leading-relaxed text-text-title">
                  <code>{error.stackTrace}</code>
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* Resolution Info */}
        {isResolved && (
          <div className="mb-6 rounded-lg border border-success/30 bg-success/10 p-4">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-success">
              <Check size={16} />
              Informações de Resolução
            </h4>
            {error.resolvedAt && (
              <p className="text-sm text-text-secondary">
                Resolvido em: {formatDateTime(error.resolvedAt)}
              </p>
            )}
            {error.resolvedBy && (
              <p className="text-sm text-text-secondary">Resolvido por: {error.resolvedBy}</p>
            )}
            {error.notes && (
              <div className="mt-2">
                <p className="text-sm font-medium text-text-title">Notas:</p>
                <p className="text-sm text-text-secondary">{error.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Resolve Form */}
        {!isResolved && (
          <div className="mb-6 rounded-lg border border-border bg-muted/50 p-4">
            <h4 className="mb-3 text-sm font-semibold text-text-title">Marcar como Resolvido</h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione notas sobre a resolução (opcional)"
              className="mb-3 w-full rounded-lg border border-border-strong bg-bg-elevated p-3 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              rows={3}
            />
            <button
              onClick={handleResolve}
              disabled={isResolving}
              className="flex items-center gap-2 rounded-lg bg-success px-4 py-2 text-sm font-semibold text-on-tertiary shadow-sm transition-colors hover:bg-success/90 active:scale-[0.98] disabled:opacity-50"
            >
              {isResolving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Resolvendo...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Marcar como Resolvido
                </>
              )}
            </button>
          </div>
        )}

        {/* Affected Devices/Users */}
        <div>
          <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-title">
            <Smartphone size={16} />
            Dispositivos e Usuários Afetados ({error.affectedDevices})
          </h4>
          <div className="rounded-lg border border-border">
            {isLoadingDevices ? (
              <>
                {/* Celular: linhas simples no lugar da subtabela de 5 colunas */}
                <div className="divide-y divide-border lg:hidden">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex flex-col gap-1.5 px-4 py-3">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  ))}
                </div>
                <div className="hidden lg:block">
                  {/* Header da subtabela */}
                  <div className="border-b border-border bg-muted/50 px-4 py-3">
                    <div className="grid grid-cols-12 gap-4 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      <div className="col-span-3">Usuário</div>
                      <div className="col-span-3">Dispositivo</div>
                      <div className="col-span-2">Versão App</div>
                      <div className="col-span-2 text-center">Ocorrências</div>
                      <div className="col-span-2">Última Ocorrência</div>
                    </div>
                  </div>
                  <div className="divide-y divide-border">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="grid grid-cols-12 items-center gap-4 px-4 py-3">
                        <div className="col-span-3 flex items-center gap-2">
                          <Skeleton className="size-4 shrink-0 rounded-full" />
                          <div className="flex flex-1 flex-col gap-1.5">
                            <Skeleton className="h-3.5 w-24" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                        </div>
                        <div className="col-span-3 flex flex-col gap-1.5">
                          <Skeleton className="h-3.5 w-28" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                        <div className="col-span-2">
                          <Skeleton className="h-5 w-16 rounded-lg" />
                        </div>
                        <div className="col-span-2 flex justify-center">
                          <Skeleton className="h-3.5 w-8" />
                        </div>
                        <div className="col-span-2">
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : devicesError ? (
              <div className="p-8 text-center text-sm text-text-secondary">
                <Smartphone size={32} className="mx-auto mb-2 opacity-50" />
                <p className="font-medium text-text-title">Endpoint não disponível</p>
                <p className="mt-1">
                  A API ainda não implementou o endpoint de dispositivos afetados.
                </p>
                <p className="mt-1 text-xs">
                  Total de dispositivos afetados: <strong>{error.affectedDevices}</strong>
                </p>
              </div>
            ) : affectedDevices && affectedDevices.length > 0 ? (
              <>
                <ul className="divide-y divide-border lg:hidden">
                  {affectedDevices.map((device) => (
                    <ItemLista
                      key={device.deviceId}
                      titulo={device.userName}
                      apoio={`${device.deviceModel} · OS ${device.osVersion}`}
                      meta={`${device.appVersion} · ${formatDateTime(device.lastOccurrence)}`}
                      fim={
                        <>
                          <span className="text-sm font-semibold tabular-nums text-text-title">
                            {device.occurrences}
                          </span>
                          <span>ocorrências</span>
                        </>
                      }
                    />
                  ))}
                </ul>
                <div className="hidden lg:block">
                  <div className="border-b border-border bg-muted/50 px-4 py-3">
                    <div className="grid grid-cols-12 gap-4 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      <div className="col-span-3">Usuário</div>
                      <div className="col-span-3">Dispositivo</div>
                      <div className="col-span-2">Versão App</div>
                      <div className="col-span-2 text-center">Ocorrências</div>
                      <div className="col-span-2">Última Ocorrência</div>
                    </div>
                  </div>
                  <div className="divide-y divide-border">
                    {affectedDevices.map((device) => (
                      <div
                        key={device.deviceId}
                        className="grid grid-cols-12 items-center gap-4 px-4 py-3 text-sm transition-colors hover:bg-muted/50"
                      >
                        <div className="col-span-3 flex items-center gap-2">
                          <User size={16} className="text-text-secondary" />
                          <div className="flex flex-col">
                            <span className="font-medium text-text-title">{device.userName}</span>
                            <span className="text-xs text-text-secondary">{device.userId}</span>
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="flex flex-col">
                            <span className="font-medium text-text-title">
                              {device.deviceModel}
                            </span>
                            <span className="text-xs text-text-secondary">
                              OS: {device.osVersion}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-2">
                          <span className="rounded-lg bg-muted px-2 py-1 font-mono text-xs text-text-title">
                            {device.appVersion}
                          </span>
                        </div>
                        <div className="col-span-2 text-center font-semibold text-text-title">
                          {device.occurrences}
                        </div>
                        <div className="col-span-2 text-xs text-text-secondary">
                          {formatDateTime(device.lastOccurrence)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-sm text-text-secondary">
                <Smartphone size={32} className="mx-auto mb-2 opacity-50" />
                <p>Nenhum dispositivo afetado encontrado</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
